import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import axios from "axios";

import {
  Alert as MuiAlert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlined from "@mui/icons-material/TableViewOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  usePdfSettings,
  DEFAULT_PDF_COLORS,
} from "../../../layouts/PdfSettingsContext.js";
import { useBranch } from "../../../layouts/BranchContext";

// Normalize the API origin so endpoints containing /api never become /api/api/...
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://wims.revisetoexcel.com"
).replace(/\/api\/?$/, "");

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }

  return config;
});

/* =========================================================
   HELPERS
========================================================= */

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
};

const getTodayString = () => formatDateInput(new Date());

const getOneMonthAgoString = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDateInput(date);
};

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return (
      value
        .map((item) => safeText(item, ""))
        .filter(Boolean)
        .join(", ") || fallback
    );
  }

  if (typeof value === "object") {
    return safeText(
      value?.name ??
        value?.label ??
        value?.title ??
        value?.display_name ??
        value?.branch_name ??
        value?.payment_mode ??
        value?.payment_method ??
        value?.id,
      fallback
    );
  }

  return fallback;
};

const getIdString = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return typeof value === "object"
    ? String(value?.id ?? "")
    : String(value);
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatDisplayDate = (value) => {
  if (!value) return "-";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  return match
    ? `${match[3]}-${match[2]}-${match[1]}`
    : String(value);
};

const hexToRgb = (hex) => {
  const safeHex = String(hex || "#16355B")
    .replace("#", "")
    .trim();

  const normalized =
    safeHex.length >= 6
      ? safeHex.slice(0, 6)
      : safeHex.padEnd(6, "0");

  return [
    parseInt(normalized.slice(0, 2), 16) || 0,
    parseInt(normalized.slice(2, 4), 16) || 0,
    parseInt(normalized.slice(4, 6), 16) || 0,
  ];
};

const getStoredUser = () => {
  for (const key of ["user", "userData", "currentUser"]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) return JSON.parse(raw) || {};
    } catch {
      // Ignore malformed storage.
    }
  }

  return {};
};

const getAccessToken = () => {
  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token");

  if (!token) return "";
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
};

const getAuthHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: token } : {};
};

/* =========================================================
   DYNAMIC BRANCH LOGO HELPERS
========================================================= */

const getAbsoluteImageUrl = (image) => {
  if (!image) return "";

  if (
    String(image).startsWith("data:image/") ||
    /^https?:\/\//i.test(String(image))
  ) {
    return String(image);
  }

  const origin = API_BASE_URL.replace(/\/api\/?$/, "");
  const raw = String(image);

  if (raw.startsWith("/api/media/")) {
    return `${origin}${raw.replace(/^\/api/, "")}`;
  }

  if (raw.startsWith("/media/")) {
    return `${origin}${raw}`;
  }

  return `${origin}/${raw.replace(/^\/+/, "")}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const dataUrlToPng = (dataUrl) =>
  new Promise((resolve, reject) => {
    if (!dataUrl) {
      resolve("");
      return;
    }

    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width || 1;
        canvas.height = image.naturalHeight || image.height || 1;

        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = reject;
    image.src = dataUrl;
  });

const imageUrlToDataUrl = async (imageUrl) => {
  if (!imageUrl) return "";

  try {
    if (String(imageUrl).startsWith("data:image/svg+xml")) {
      return await dataUrlToPng(imageUrl);
    }

    if (String(imageUrl).startsWith("data:image/")) {
      return imageUrl;
    }

    const response = await axios.get(
      getAbsoluteImageUrl(imageUrl),
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    const dataUrl = await blobToDataUrl(response.data);

    if (String(dataUrl).startsWith("data:image/svg+xml")) {
      return await dataUrlToPng(dataUrl);
    }

    return dataUrl;
  } catch (error) {
    console.warn("Branch logo conversion error:", error);
    return "";
  }
};

const getBranchDetailsForPdf = async (branchId) => {
  if (!branchId) {
    return {
      details: null,
      branchName: "",
      branchLogo: "",
    };
  }

  try {
    const response = await API.get(
      `/api/master/branch/${branchId}/`
    );

    const details = response?.data || {};

    const image =
      details?.image ||
      details?.logo ||
      details?.branch_logo ||
      details?.branchLogo ||
      "";

    return {
      details,
      branchName:
        details?.name ||
        details?.branch_name ||
        details?.branchName ||
        "",
      branchLogo: await imageUrlToDataUrl(image),
    };
  } catch (error) {
    console.error("Branch details API error:", error);

    return {
      details: null,
      branchName: "",
      branchLogo: "",
    };
  }
};

const getOrganizationName = (user, branchDetails) =>
  safeText(
    user?.organization_name ||
      user?.organizationName ||
      (typeof user?.organization === "object"
        ? user?.organization?.name
        : "") ||
      branchDetails?.organization?.name ||
      branchDetails?.organization_name ||
      "",
    ""
  );

/* =========================================================
   COMMON PDF GENERATOR
========================================================= */

const getSummaryEntries = (summary) => {
  if (!summary) return [];

  if (Array.isArray(summary)) {
    return summary.map((item, index) => [
      item?.label || item?.name || `Summary ${index + 1}`,
      item?.value ?? item?.amount ?? item?.count ?? "-",
    ]);
  }

  if (typeof summary === "object") {
    return Object.entries(summary).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        typeof value !== "object"
    );
  }

  return [["Summary", summary]];
};

const createCommonReportPdf = ({
  title,
  rows = [],
  columns = [],
  widths = {},
  themeColor = DEFAULT_PDF_COLORS.primary,
  textColor = DEFAULT_PDF_COLORS.bodyText,
  organizationName = "",
  branchName = "",
  branchLogo = "",
  filters = [],
  summary = null,
}) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const themeRgb = hexToRgb(themeColor);
  const textRgb = hexToRgb(textColor);

  const drawPdfHeader = () => {
    doc.setFillColor(...themeRgb);
    doc.rect(0, 0, pageWidth, 34, "F");

    if (branchLogo) {
      try {
        doc.addImage(
          branchLogo,
          "PNG",
          margin,
          5,
          21,
          21
        );
      } catch (error) {
        console.warn(
          "Unable to add branch logo:",
          error
        );
      }
    }

    doc.setTextColor(...hexToRgb(DEFAULT_PDF_COLORS.headerText));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(
      organizationName || "Organization",
      pageWidth / 2,
      11,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      branchName || "",
      pageWidth / 2,
      17,
      { align: "center" }
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(
      title,
      pageWidth / 2,
      24,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(
      `Generated: ${new Date().toLocaleString("en-GB")}`,
      pageWidth - margin,
      30,
      { align: "right" }
    );
  };

  drawPdfHeader();

  let cursorY = 40;

  const validFilters = (filters || []).filter(
    (item) =>
      item?.value !== undefined &&
      item?.value !== null &&
      item?.value !== ""
  );

  if (validFilters.length) {
    doc.setTextColor(...textRgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Filters", margin, cursorY);
    cursorY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);

    const filterText = validFilters
      .map(
        (item) =>
          `${item.label}: ${safeText(item.value)}`
      )
      .join("   |   ");

    const wrapped = doc.splitTextToSize(
      filterText,
      pageWidth - margin * 2
    );

    doc.text(wrapped, margin, cursorY);
    cursorY += wrapped.length * 4 + 2;
  }

  const summaryEntries = getSummaryEntries(summary);

  if (summaryEntries.length) {
    doc.setTextColor(...textRgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Summary", margin, cursorY);
    cursorY += 5;

    autoTable(doc, {
      startY: cursorY,
      head: [["Metric", "Value"]],
      body: summaryEntries.map(
        ([label, value]) => [
          safeText(label),
          safeText(value),
        ]
      ),
      theme: "grid",
      tableWidth: "wrap",
      margin: {
        left: margin,
        right: margin,
      },
      styles: {
        font: "helvetica",
        fontSize: 7,
        textColor: textRgb,
        cellPadding: 1.5,
      },
      headStyles: {
        fillColor: themeRgb,
        textColor: hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText),
        fontStyle: "bold",
      },
    });

    cursorY =
      (doc.lastAutoTable?.finalY || cursorY) +
      6;
  }

  const head = columns.map(
    (column) => column.label
  );

  const body = rows.length
    ? rows.map((row, index) =>
        columns.map((column) =>
          column.render
            ? column.render(row, index)
            : safeText(row?.[column.key])
        )
      )
    : [
        columns.map((_, index) =>
          index === 0 ? "No data found" : "-"
        ),
      ];

  const columnStyles = {};

  columns.forEach((column, index) => {
    if (widths[column.key]) {
      columnStyles[index] = {
        cellWidth: widths[column.key],
      };
    }
  });

  autoTable(doc, {
    startY: cursorY,
    head: [head],
    body,
    theme: "grid",
    margin: {
      top: 39,
      right: margin,
      bottom: 12,
      left: margin,
    },
    styles: {
      font: "helvetica",
      fontSize: 7.2,
      textColor: textRgb,
      cellPadding: 1.8,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: themeRgb,
      textColor: hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText),
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles,
    willDrawPage: drawPdfHeader,
  });

  const pageCount =
    doc.internal.getNumberOfPages();

  for (
    let pageNo = 1;
    pageNo <= pageCount;
    pageNo += 1
  ) {
    doc.setPage(pageNo);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...textRgb);

    doc.text(
      title,
      margin,
      pageHeight - 6
    );

    doc.text(
      `Page ${pageNo} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 6,
      { align: "right" }
    );
  }

  return doc;
};

/* =========================================================
   TABLE
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  overflowX: "auto",
  overflowY: "auto",
  maxWidth: "100%",
  "&::-webkit-scrollbar": {
    width: 7,
    height: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.24)"
        : "rgba(15,23,42,0.22)",
    borderRadius: 10,
  },
  scrollbarWidth: "thin",
}));

const ReportTable = ({
  rows = [],
  columns = [],
  loading = false,
  searchPlaceholder = "Search report...",
  title = "Report records",
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        safeText(value, "")
          .toLowerCase()
          .includes(keyword)
      )
    );
  }, [rows, search]);

  useEffect(() => {
    setPage(0);
  }, [search, rows.length]);

  const visibleRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 2.5,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
    

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
        }}
      >
        <CustomTableScrollbar>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 980,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    minWidth: 58,
                    fontWeight: 700,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  #
                </TableCell>

                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      minWidth:
                        column.minWidth || 140,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      backgroundColor:
                        "primary.main",
                      color:
                        "primary.contrastText",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <CircularProgress size={30} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      Loading report...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length ? (
                visibleRows.map((row, index) => (
                  <TableRow
                    key={
                      row?.id ??
                      `${page}-${index}`
                    }
                    hover
                  >
                    <TableCell>
                      {page * rowsPerPage +
                        index +
                        1}
                    </TableCell>

                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          whiteSpace:
                            column.nowrap
                              ? "nowrap"
                              : "normal",
                        }}
                      >
                        {column.render
                          ? column.render(
                              row,
                              index
                            )
                          : safeText(
                              row?.[column.key]
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Typography fontWeight={600}>
                      No records found
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.disabled"
                    >
                      Try changing the filters or search text.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomTableScrollbar>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, nextPage) =>
            setPage(nextPage)
          }
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(
              Number(event.target.value)
            );
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

/* =========================================================
   FILTER MODAL
========================================================= */
const FilterModal = ({
  open,
  onClose,
  title,
  children,
  onApply,
  onReset,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    BackdropProps={{
      sx: {
        backgroundColor: "rgba(15,23,42,0.58)",
        backdropFilter: "blur(3px)",
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: {
          xs: "94%",
          sm: 680,
          md: 760,
        },
        maxHeight: "90vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: 3,
        outline: "none",
        boxShadow: 24,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 3,
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
            }}
          >
            Configure filters and apply the report.
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          maxHeight: "60vh",
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,

            // Allows direct filter elements returned
            // inside children to participate in the layout.
            "& > *": {
              minWidth: 0,
            },
          }}
        >
          {children}
        </Box>

        {/* FOOTER */}
        <Divider
          sx={{
            my: 2.5,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            alignItems: {
              xs: "stretch",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 1.5,
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            py: 1.5,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            onClick={onReset}
            sx={{
              textTransform: "none",
              color: "text.primary",
              fontWeight: 600,
              alignSelf: {
                xs: "flex-start",
                sm: "auto",
              },
            }}
          >
            Reset
          </Button>

          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
              width: {
                xs: "100%",
                sm: "auto",
              },
            }}
          >
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={<FilterAltOutlined />}
              onClick={onApply}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                fontWeight: 600,
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </Modal>
);
/* =========================================================
   PDF COLOR MODAL
========================================================= */

const PdfColorModal = ({
  open,
  onClose,
  onGenerate,
  reportTitle,
  }) => {
  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  const activeThemeColor =
    pdfThemeColor || DEFAULT_PDF_COLORS.primary;
  const activeTextColor =
    pdfTextColor || DEFAULT_PDF_COLORS.bodyText;

  const [themeColor, setThemeColor] =
    useState(pdfThemeColor);
  const [textColor, setTextColor] =
    useState(pdfTextColor);

  useEffect(() => {
    if (open) {
      setThemeColor(pdfThemeColor);
      setTextColor(pdfTextColor);
    }
  }, [
    open,
    pdfThemeColor,
    pdfTextColor,
  ]);

  return (
    <ReportPdfColorModal
  open={open}
  onClose={onClose}
  themeColor={themeColor || activeThemeColor}
  setThemeColor={setThemeColor}
  textColor={textColor || activeTextColor}
  setTextColor={setTextColor}
  reportTitle={reportTitle || "Report Title"}
  onGenerate={() => {
              updatePdfThemeColor?.(themeColor);
              updatePdfTextColor?.(textColor);
              onGenerate(themeColor, textColor);
            }}
/>
  );
};

/* =========================================================
   REPORT HEADER / TABS
========================================================= */

const ReportHeader = ({
  title,
  subtitle,
  onFilter,
  onGenerate,
  onExcel,
  onPdf,
  onReset,
  loading,
  disableExport,
  showPdf = true,
}) => (
  <Box
    sx={{
      px: { xs: 1.5, md: 2.5 },
      py: { xs: 1.5, md: 2 },
      display: "flex",
      flexDirection: {
        xs: "column",
        lg: "row",
      },
      alignItems: {
        xs: "stretch",
        lg: "center",
      },
      justifyContent: "flex-end",
      gap: 2,
    }}
  >
    
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      useFlexGap
      flexWrap="wrap"
    >
      <Button
        variant="contained"
        startIcon={<FilterAltOutlined />}
        onClick={onFilter}
        sx={{ textTransform: "none" }}
      >
        Filters
      </Button>

      <Button
        variant="contained"
        startIcon={
          loading ? (
            <CircularProgress
              size={17}
              color="inherit"
            />
          ) : (
            <AssessmentOutlined />
          )
        }
        disabled={loading}
        onClick={onGenerate}
        sx={{ textTransform: "none" }}
      >
        {loading ? "Generating..." : "Generate Report"}
      </Button>

      <Button
        variant="contained"
        color="success"
        startIcon={<TableViewOutlined />}
        disabled={loading || disableExport}
        onClick={onExcel}
        sx={{ textTransform: "none" }}
      >
        Export Excel
      </Button>

      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        disabled={loading}
        onClick={onReset}
        sx={{ textTransform: "none" }}
      >
        Reset
      </Button>
    </Stack>
  </Box>
);

const ReportTabs = ({ value, onChange }) => (
 <Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={value}
    onChange={(_, nextValue) => onChange(nextValue)}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-flexContainer": {
        gap: 0.5,
      },

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",
        px: {
          xs: 1.5,
          sm: 2.2,
        },
        borderRadius: 1.5,
        color: "text.primary",
        fontWeight: 600,
        textTransform: "none",
        fontSize: {
          xs: 13,
          sm: 14,
        },
        opacity: 1,
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },

      "& .MuiTabs-scrollButtons": {
        color: "text.primary",

        "&.Mui-disabled": {
          opacity: 0.3,
        },
      },
    }}
  >
    <Tab
      value="fee-deposit"
      label="Fee Deposit Report"
    />

    <Tab
      value="outstanding"
      label="Outstanding Fee Report"
    />

  </Tabs>
</Box>
);

/* =========================================================
   COMMON BRANCH HOOK DATA
========================================================= */

const useGlobalBranchReportData = () => {
  const user = useMemo(() => getStoredUser(), []);
  const branchContext = useBranch() || {};

  const {
    selectedBranchId: contextBranchId,
    branchList: contextBranchList = [],
    organizationId: contextOrganizationId,
  } = branchContext;

  const organizationId = String(
    contextOrganizationId ||
      user?.organization_id ||
      ""
  );

  const selectedBranchId = String(
    contextBranchId ||
      user?.branch_id ||
      ""
  );

  const branchList = contextBranchList || [];

  const selectedBranchName = useMemo(() => {
    const branch = branchList.find(
      (item) =>
        String(
          item?.id ?? item?.branch_id
        ) === String(selectedBranchId)
    );

    return safeText(
      branch?.name ?? branch?.branch_name,
      ""
    );
  }, [
    branchList,
    selectedBranchId,
  ]);

  const isMainBranchAdmin =
    !!user?.is_main_branch_admin;

  const [branchDetails, setBranchDetails] =
    useState(null);
  const [branchLogo, setBranchLogo] =
    useState("");

  useEffect(() => {
    let mounted = true;

    const loadBranchDetails = async () => {
      const result =
        await getBranchDetailsForPdf(
          selectedBranchId
        );

      if (!mounted) return;

      setBranchDetails(result.details);
      setBranchLogo(result.branchLogo);
    };

    loadBranchDetails();

    return () => {
      mounted = false;
    };
  }, [selectedBranchId]);

  return {
    user,
    organizationId,
    selectedBranchId,
    branchList,
    selectedBranchName,
    isMainBranchAdmin,
    branchDetails,
    branchLogo,
  };
};

/* =========================================================
   FEE DEPOSIT REPORT
========================================================= */

const FeeDepositReport = () => {
  const {
    user,
    organizationId,
    selectedBranchId,
    branchList,
    selectedBranchName,
    isMainBranchAdmin,
    branchDetails,
    branchLogo,
  } = useGlobalBranchReportData();

  const [fromDate, setFromDate] =
    useState(getOneMonthAgoString());
  const [toDate, setToDate] =
    useState(getTodayString());
  const [admissionType, setAdmissionType] =
    useState("all");
  const [selectedAdmissionNo, setSelectedAdmissionNo] =
    useState("");
  const [admissionOptions, setAdmissionOptions] =
    useState([]);
  const [paymentModeType, setPaymentModeType] =
    useState("all");
  const [selectedPaymentMode, setSelectedPaymentMode] =
    useState("");
  const [paymentMethods, setPaymentMethods] =
    useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAdmissions, setLoadingAdmissions] =
    useState(false);
  const [loadingModes, setLoadingModes] =
    useState(false);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const fetchAdmissions = useCallback(async () => {
    if (!organizationId) {
      setAdmissionOptions([]);
      return;
    }

    setLoadingAdmissions(true);

    try {
      const params = {
        organization: organizationId,
      };

      if (selectedBranchId) {
        params.branch = selectedBranchId;
      }

      const response = await API.get(
        "/api/fee_details/fee-deposit/",
        { params }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      const unique = new Map();

      data.forEach((item) => {
        const admissionNo = safeText(
          item?.admission_code ??
            item?.admission_no,
          ""
        );

        if (admissionNo) {
          unique.set(admissionNo, {
            admission_no: admissionNo,
            student_name: safeText(
              item?.student_name ??
                item?.student?.name,
              ""
            ),
          });
        }
      });

      setAdmissionOptions(
        Array.from(unique.values())
      );
    } catch (err) {
      console.error(err);
      setAdmissionOptions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, [
    organizationId,
    selectedBranchId,
  ]);

  useEffect(() => {
    fetchAdmissions();
  }, [fetchAdmissions]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoadingModes(true);

      try {
        const response = await API.get(
          "/api/master/payment-methods/"
        );

        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        setPaymentMethods(data.filter(Boolean));
      } catch (err) {
        console.error(err);
        setPaymentMethods([]);
      } finally {
        setLoadingModes(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  const buildPayload = useCallback(() => {
    const payload = {
      fromDate,
      toDate,
      organizationId,
    };

    if (selectedBranchId) {
      payload.branch = selectedBranchId;
    }

    if (
      admissionType === "selected" &&
      selectedAdmissionNo
    ) {
      payload.admissionNo =
        selectedAdmissionNo;
    }

    if (
      paymentModeType === "selected" &&
      selectedPaymentMode
    ) {
      payload.paymentMode = selectedPaymentMode;
    }

    return payload;
  }, [
    fromDate,
    toDate,
    organizationId,
    selectedBranchId,
    admissionType,
    selectedAdmissionNo,
    paymentModeType,
    selectedPaymentMode,
  ]);

  const fetchFeeDepositRows = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      setSummary(null);
      return [];
    }

    setLoading(true);
    setError("");

    try {
      const response = await API.post(
        "/api/reports/fee-deposit-report/",
        buildPayload()
      );

      const responseData = response?.data || {};
      const data = Array.isArray(responseData)
        ? responseData
        : responseData?.results || [];

      const normalizedRows = data.map((item, index) => ({
        id: item?.id ?? `fee-${index}`,
        payment_date:
          item?.payment_date ?? item?.date,
        admission_no:
          item?.admission_no ??
          item?.admission_code,
        student_name:
          item?.student_name ??
          item?.student?.name ??
          "",
        installment_no:
          item?.installment_no,
        payment_mode:
          item?.payment_mode_name ??
          item?.payment_mode ??
          item?.payment_method,
        paid_amount:
          item?.paid_amount ??
          item?.amount ??
          0,
        reference_no:
          item?.reference_no,
        bank_name:
          item?.bank_name,
      }));

      setRows(normalizedRows);
      setSummary(responseData?.summary || null);

      if (!normalizedRows.length) {
        setError(
          "No fee deposit records found for the selected filters."
        );
      }

      return normalizedRows;
    } catch (err) {
      console.error(err);
      setRows([]);
      setSummary(null);
      setError(
        safeText(
          err?.response?.data?.detail ??
            err?.response?.data?.message ??
            err?.message,
          "Failed to load Fee Deposit Report."
        )
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [buildPayload, organizationId]);
  useEffect(() => {
    fetchFeeDepositRows();
  }, [fetchFeeDepositRows]);

  const generateReport = async () => {
    const refreshedRows =
      await fetchFeeDepositRows();

    if (refreshedRows.length) {
      // The Color Picker component reads the centralized/default PDF
      // colors from PdfSettingsContext when it opens.
      setColorOpen(true);
    }
  };

  const exportExcel = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await API.post(
        "/api/reports/reports/fee/export/excel/",
        buildPayload(),
        { responseType: "blob" }
      );

      const url = URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.download =
        `Fee_Deposit_Report_${fromDate}_to_${toDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(
        "Failed to download Fee Deposit Excel."
      );
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async (
    themeColor,
    textColor
  ) => {
    setColorOpen(false);
    setLoading(true);
    setError("");

    try {
      const data = rows;

      const branchResult =
        await getBranchDetailsForPdf(
          selectedBranchId
        );

      const details =
        branchResult.details || branchDetails;

      const doc = createCommonReportPdf({
        title: "Fee Deposit Report",
        rows: data,
        columns: [
          {
            key: "payment_date",
            label: "Payment Date",
            render: (row) =>
              formatDisplayDate(
                row?.payment_date ?? row?.date
              ),
          },
          {
            key: "admission_no",
            label: "Admission No.",
          },
          {
            key: "student_name",
            label: "Student Name",
          },
          {
            key: "installment_no",
            label: "Installment No.",
          },
          {
            key: "payment_mode",
            label: "Payment Mode",
          },
          {
            key: "paid_amount",
            label: "Paid Amount",
            render: (row) =>
              formatCurrency(
                row?.paid_amount ??
                  row?.amount
              ),
          },
          {
            key: "reference_no",
            label: "Reference No.",
          },
          {
            key: "bank_name",
            label: "Bank Name",
          },
        ],
        widths: {
          payment_date: 24,
          admission_no: 30,
          student_name: 42,
          installment_no: 28,
          payment_mode: 30,
          paid_amount: 30,
          reference_no: 35,
          bank_name: 35,
        },
        themeColor,
        textColor,
        organizationName: getOrganizationName(
          user,
          details
        ),
        branchName:
          branchResult.branchName ||
          selectedBranchName,
        branchLogo:
          branchResult.branchLogo ||
          branchLogo,
        filters: [
          {
            label: "From Date",
            value: fromDate,
          },
          {
            label: "To Date",
            value: toDate,
          },
          {
            label: "Admission",
            value:
              admissionType === "selected"
                ? selectedAdmissionNo
                : "All",
          },
          {
            label: "Payment Mode",
            value:
              paymentModeType === "selected"
                ? selectedPaymentMode
                : "All",
          },
        ],
        summary,
      });

      const url = URL.createObjectURL(
        doc.output("blob")
      );

      const popup = window.open(
        url,
        "_blank"
      );

      if (!popup) {
        const link = document.createElement("a");
        link.href = url;
        link.download =
          `Fee_Deposit_Report_${fromDate}_to_${toDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(
        () => URL.revokeObjectURL(url),
        10000
      );
    } catch (err) {
      console.error(err);
      setError(
        safeText(
          err?.message,
          "Failed to generate Fee Deposit PDF."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFromDate(getOneMonthAgoString());
    setToDate(getTodayString());
    setAdmissionType("all");
    setSelectedAdmissionNo("");
    setPaymentModeType("all");
    setSelectedPaymentMode("");
    setSummary(null);
    setError("");
    setTimeout(() => fetchFeeDepositRows(), 0);
  };

  const columns = [
    {
      key: "payment_date",
      label: "Payment Date",
      nowrap: true,
      render: (row) =>
        formatDisplayDate(row?.payment_date),
    },
    {
      key: "admission_no",
      label: "Admission No.",
    },
    {
      key: "student_name",
      label: "Student Name",
    },
    {
      key: "installment_no",
      label: "Installment No.",
      nowrap: true,
    },
    {
      key: "payment_mode",
      label: "Payment Mode",
    },
    {
      key: "paid_amount",
      label: "Paid Amount",
      nowrap: true,
      render: (row) =>
        formatCurrency(row?.paid_amount),
    },
    {
      key: "reference_no",
      label: "Reference No.",
    },
    {
      key: "bank_name",
      label: "Bank Name",
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
      
        backgroundColor: "transparent",
      }}
    >
      <ReportHeader
        title="Fee Deposit Report"
        subtitle={`${
          selectedBranchName ||
          `Branch ${selectedBranchId}`
        } • ${fromDate} to ${toDate}`}
        onFilter={() => setFilterOpen(true)}
        onGenerate={generateReport}
        onExcel={exportExcel}
        onPdf={() => setColorOpen(true)}
        onReset={reset}
        loading={loading}
        disableExport={!rows.length}
        showPdf={false}
      />

   
      {error && (
        <MuiAlert
          severity="error"
          sx={{ m: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </MuiAlert>
      )}

      <CardContent>
        <ReportTable
          title="Fee deposit records"
          rows={rows}
          columns={columns}
          loading={loading}
          searchPlaceholder="Search fee deposit..."
        />
      </CardContent>

<FilterModal
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  title="Fee Deposit Filters"
  onApply={() => setFilterOpen(false)}
  onReset={reset}
>
<Box
  sx={{
    display: "flex",
    flexDirection: "column",
    gap: 3,
    pt: 1,
    pb: 5,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  }}
>
    {/* Student Admission */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Student Admission
      </Typography>

      <RadioGroup
        row
        value={admissionType}
        onChange={(event) => {
          const value = event.target.value;
          setAdmissionType(value);

          if (value === "all") {
            setSelectedAdmissionNo("");
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
          },
          gap: 1.5,
        }}
      >
        {[
          { value: "all", label: "All admissions" },
          { value: "selected", label: "Specific admission" },
        ].map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
            sx={{
              m: 0,
              minWidth: 0,
              minHeight: 48,
              px: 1.5,
              py: 0.5,
              border: "1px solid",
              borderColor:
                admissionType === option.value
                  ? "primary.main"
                  : "divider",
              borderRadius: 2,
              bgcolor:
                admissionType === option.value
                  ? "action.selected"
                  : "background.paper",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: "primary.main",
              },
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                fontWeight:
                  admissionType === option.value ? 600 : 400,
              },
            }}
          />
        ))}
      </RadioGroup>

      {admissionType === "selected" && (
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Admission number</InputLabel>
          <Select
            value={selectedAdmissionNo}
            label="Admission number"
            onChange={(event) =>
              setSelectedAdmissionNo(String(event.target.value))
            }
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 320, borderRadius: 2 },
              },
            }}
          >
            <MenuItem value="">Select admission</MenuItem>

            {loadingAdmissions ? (
              <MenuItem disabled>Loading admissions...</MenuItem>
            ) : (
              admissionOptions.map((item) => (
                <MenuItem
                  key={item.admission_no}
                  value={item.admission_no}
                >
                  {item.admission_no} — {item.student_name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}
    </Box>

   

    {/* Payment Method */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Payment Method
      </Typography>

      <RadioGroup
        row
        value={paymentModeType}
        onChange={(event) => {
          const value = event.target.value;
          setPaymentModeType(value);

          if (value === "all") {
            setSelectedPaymentMode("");
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
          },
          gap: 1.5,
        }}
      >
        {[
          { value: "all", label: "All methods" },
          { value: "selected", label: "Specific method" },
        ].map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
            sx={{
              m: 0,
              minWidth: 0,
              minHeight: 48,
              px: 1.5,
              py: 0.5,
              border: "1px solid",
              borderColor:
                paymentModeType === option.value
                  ? "primary.main"
                  : "divider",
              borderRadius: 2,
              bgcolor:
                paymentModeType === option.value
                  ? "action.selected"
                  : "background.paper",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: "primary.main",
              },
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                fontWeight:
                  paymentModeType === option.value ? 600 : 400,
              },
            }}
          />
        ))}
      </RadioGroup>

      {paymentModeType === "selected" && (
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Payment method</InputLabel>
          <Select
            value={selectedPaymentMode}
            label="Payment method"
            onChange={(event) =>
              setSelectedPaymentMode(String(event.target.value))
            }
          >
            <MenuItem value="">Select payment method</MenuItem>

            {loadingModes ? (
              <MenuItem disabled>Loading payment methods...</MenuItem>
            ) : (
              paymentMethods.map((item) => (
                <MenuItem
                  key={getIdString(item?.id)}
                  value={getIdString(item?.id)}
                >
                  {safeText(
                    item?.name ??
                      item?.payment_mode ??
                      item?.payment_method
                  )}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}
    </Box>



    {/* Payment Date Range */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        Payment Date Range
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 2 }}
      >
        Select the start and end dates for the report.
      </Typography>

     
<Box
  sx={{
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: 2,
    width: "100%",
    alignItems: "flex-start",
  }}
>
  <TextField
    fullWidth
    size="small"
    label="From date"
    type="date"
    value={fromDate}
    onChange={(event) => setFromDate(event.target.value)}
    inputRef={fromDateRef}
    InputLabelProps={{ shrink: true }}
    inputProps={{ max: toDate || undefined }}
    sx={{
      flex: 1,
      minWidth: 0,
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        minHeight: 44,
      },
    }}
  />

  <TextField
    fullWidth
    size="small"
    label="To date"
    type="date"
    value={toDate}
    onChange={(event) => setToDate(event.target.value)}
    inputRef={toDateRef}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min: fromDate || undefined }}
    sx={{
      flex: 1,
      minWidth: 0,
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        minHeight: 44,
      },
    }}
  />
</Box>

    </Box>
  </Box>
</FilterModal>


      <PdfColorModal reportTitle={"Fee Deposit Report"}
        open={colorOpen}
        onClose={() => setColorOpen(false)}
        onGenerate={generatePdf}
      />
    </Card>
  );
};

/* =========================================================
   OUTSTANDING FEE REPORT
========================================================= */

const OutstandingFeeReport = () => {
  const {
    user,
    organizationId,
    selectedBranchId,
    branchList,
    selectedBranchName,
    isMainBranchAdmin,
    branchDetails,
    branchLogo,
  } = useGlobalBranchReportData();

  const [fromDate, setFromDate] =
    useState(getOneMonthAgoString());
  const [toDate, setToDate] =
    useState(getTodayString());
  const [minOutstanding, setMinOutstanding] =
    useState("");
  const [admissionType, setAdmissionType] =
    useState("all");
  const [selectedAdmissionNo, setSelectedAdmissionNo] =
    useState("");
  const [admissionOptions, setAdmissionOptions] =
    useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAdmissions, setLoadingAdmissions] =
    useState(false);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const fetchAdmissions = useCallback(async () => {
    if (!organizationId) {
      setAdmissionOptions([]);
      return;
    }

    setLoadingAdmissions(true);

    try {
      const params = {
        organization: organizationId,
      };

      if (selectedBranchId) {
        params.branch = selectedBranchId;
      }

      const response = await API.get(
        "/api/fee_details/fee-deposit/",
        { params }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      const unique = new Map();

      data.forEach((item) => {
        const admissionNo = safeText(
          item?.admission_code ??
            item?.admission_no,
          ""
        );

        if (admissionNo) {
          unique.set(admissionNo, {
            admission_no: admissionNo,
            student_name: safeText(
              item?.student_name ??
                item?.student?.name,
              ""
            ),
          });
        }
      });

      setAdmissionOptions(
        Array.from(unique.values())
      );
    } catch (err) {
      console.error(err);
      setAdmissionOptions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, [
    organizationId,
    selectedBranchId,
  ]);

  useEffect(() => {
    fetchAdmissions();
  }, [fetchAdmissions]);

  const buildPayload = useCallback(() => {
    const payload = {
      fromDate,
      toDate,
      organizationId: Number(organizationId),
    };

    if (selectedBranchId) {
      payload.branch = Number(selectedBranchId);
    }

    if (minOutstanding !== "") {
      payload.minOutstanding = Number(minOutstanding);
    }

    return payload;
  }, [
    fromDate,
    toDate,
    organizationId,
    minOutstanding,
    selectedBranchId,
  ]);

  const fetchOutstandingRows = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      setSummary(null);
      return [];
    }

    setLoading(true);
    setError("");

    try {
      const response = await API.post(
        "/api/reports/outstanding-fee-report/",
        buildPayload()
      );

      const responseData = response?.data || {};
      const data = Array.isArray(responseData)
        ? responseData
        : responseData?.results || [];

      const normalizedRows = data.map((item, index) => ({
          id: item?.id ?? `outstanding-${index}`,
          admission_no:
            item?.admission_code ?? item?.admission_no,
          student_name: item?.student_name ?? "",
          mobile: item?.mobile ?? "",
          total_fee: item?.total_fee ?? 0,
          paid:
            item?.paid_amount ?? item?.paid ?? item?.amount_paid ?? 0,
          outstanding:
            item?.outstanding_amount ??
            item?.outstanding ??
            item?.pending_fee ??
            0,
          installments:
            item?.installment_count ?? item?.installments ?? 0,
          last_updated:
            item?.last_updated ?? item?.admission_date ?? "",
        }));

      setRows(normalizedRows);
      setSummary(responseData?.summary || null);

      if (!normalizedRows.length) {
        setError(
          "No outstanding fee records found for the selected filters."
        );
      }

      return normalizedRows;
    } catch (err) {
      console.error(err);
      setRows([]);
      setError(
        safeText(
          err?.response?.data?.detail ??
            err?.response?.data?.message ??
            err?.message,
          "Failed to load Outstanding Fee Report."
        )
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [buildPayload, organizationId]);

  useEffect(() => {
    fetchOutstandingRows();
  }, [fetchOutstandingRows]);

  const generateReport = async () => {
    const refreshedRows =
      await fetchOutstandingRows();

    if (refreshedRows.length) {
      // The Color Picker component reads the centralized/default PDF
      // colors from PdfSettingsContext when it opens.
      setColorOpen(true);
    }
  };

  const exportExcel = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await API.post(
        "/api/reports/reports/outstanding-fee-report/export/excel/",
        buildPayload(),
        { responseType: "blob" }
      );

      const url = URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.download =
        `Outstanding_Fee_Report_${fromDate}_to_${toDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(
        "Failed to download Outstanding Fee Excel."
      );
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async (
    themeColor,
    textColor
  ) => {
    setColorOpen(false);
    setLoading(true);
    setError("");

    try {
      const data = rows;

      const branchResult =
        await getBranchDetailsForPdf(
          selectedBranchId
        );

      const details =
        branchResult.details || branchDetails;

      const doc = createCommonReportPdf({
        title: "Outstanding Fee Report",
        rows: data,
        columns: [
          {
            key: "admission_no",
            label: "Admission No.",
          },
          {
            key: "student_name",
            label: "Student Name",
          },
          {
            key: "mobile",
            label: "Mobile",
          },
          {
            key: "total_fee",
            label: "Total Fee",
            render: (row) =>
              formatCurrency(row?.total_fee),
          },
          {
            key: "paid",
            label: "Paid",
            render: (row) =>
              formatCurrency(
                row?.paid ??
                  row?.amount_paid
              ),
          },
          {
            key: "outstanding",
            label: "Outstanding",
            render: (row) =>
              formatCurrency(
                row?.outstanding ??
                  row?.pending_fee
              ),
          },
          {
            key: "installments",
            label: "Installments",
          },
          {
            key: "last_updated",
            label: "Last Updated",
            render: (row) =>
              formatDisplayDate(
                row?.last_updated
              ),
          },
        ],
        widths: {
          admission_no: 32,
          student_name: 45,
          mobile: 28,
          total_fee: 30,
          paid: 30,
          outstanding: 32,
          installments: 30,
          last_updated: 32,
        },
        themeColor,
        textColor,
        organizationName: getOrganizationName(
          user,
          details
        ),
        branchName:
          branchResult.branchName ||
          selectedBranchName,
        branchLogo:
          branchResult.branchLogo ||
          branchLogo,
        filters: [
          {
            label: "From Date",
            value: fromDate,
          },
          {
            label: "To Date",
            value: toDate,
          },
          {
            label: "Minimum Outstanding",
            value: minOutstanding || "0",
          },
          {
            label: "Admission",
            value:
              admissionType === "selected"
                ? selectedAdmissionNo
                : "All",
          },
        ],
        summary,
      });

      const url = URL.createObjectURL(
        doc.output("blob")
      );

      const popup = window.open(
        url,
        "_blank"
      );

      if (!popup) {
        const link = document.createElement("a");
        link.href = url;
        link.download =
          `Outstanding_Fee_Report_${fromDate}_to_${toDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(
        () => URL.revokeObjectURL(url),
        10000
      );
    } catch (err) {
      console.error(err);
      setError(
        safeText(
          err?.message,
          "Failed to generate Outstanding Fee PDF."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFromDate(getOneMonthAgoString());
    setToDate(getTodayString());
    setMinOutstanding("");
    setAdmissionType("all");
    setSelectedAdmissionNo("");
    setRows([]);
    setSummary(null);
    setError("");
    setTimeout(() => fetchOutstandingRows(), 0);
  };

  const columns = [
    {
      key: "admission_no",
      label: "Admission No.",
    },
    {
      key: "student_name",
      label: "Student Name",
    },
    {
      key: "mobile",
      label: "Mobile",
    },
    {
      key: "total_fee",
      label: "Total Fee",
      render: (row) =>
        formatCurrency(row?.total_fee),
    },
    {
      key: "paid",
      label: "Paid",
      render: (row) =>
        formatCurrency(row?.paid),
    },
    {
      key: "outstanding",
      label: "Outstanding",
      render: (row) =>
        formatCurrency(row?.outstanding),
    },
    {
      key: "installments",
      label: "Installments",
    },
    {
      key: "last_updated",
      label: "Last Updated",
      render: (row) =>
        formatDisplayDate(row?.last_updated),
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        
        backgroundColor: "transparent",
      }}
    >
      <ReportHeader
        title="Outstanding Fee Report"
        subtitle={`${
          selectedBranchName ||
          `Branch ${selectedBranchId}`
        } • ${fromDate} to ${toDate}`}
        onFilter={() => setFilterOpen(true)}
        onGenerate={generateReport}
        onExcel={exportExcel}
        onPdf={() => setColorOpen(true)}
        onReset={reset}
        loading={loading}
        disableExport={!rows.length}
        showPdf={false}
      />

  

      {error && (
        <MuiAlert
          severity="error"
          sx={{ m: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </MuiAlert>
      )}

      <CardContent>
        <ReportTable
          title="Outstanding fee records"
          rows={rows}
          columns={columns}
          loading={loading}
          searchPlaceholder="Search outstanding fee..."
        />
      </CardContent>

<FilterModal
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  title="Outstanding Fee Filters"
  onApply={() => setFilterOpen(false)}
  onReset={reset}
>

<Box
  sx={{
    display: "flex",
    flexDirection: "column",
    gap: 3,
    pt: 1,
    pb: 2,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  }}
>
    {/* Minimum Outstanding */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Outstanding Amount
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Minimum Outstanding"
        type="number"
        value={minOutstanding}
        onChange={(event) => setMinOutstanding(event.target.value)}
        inputProps={{ min: 0 }}
        placeholder="Enter minimum amount"
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 44,
            borderRadius: 2,
          },
        }}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Show records with outstanding fees equal to or above this amount.
      </Typography>
    </Box>

 

    {/* Student Admission */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Student Admission
      </Typography>

      <RadioGroup
        row
        value={admissionType}
        onChange={(event) => {
          const value = event.target.value;
          setAdmissionType(value);

          if (value === "all") {
            setSelectedAdmissionNo("");
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          { value: "all", label: "All admissions" },
          { value: "selected", label: "Specific admission" },
        ].map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
            sx={{
              m: 0,
              minWidth: 0,
              minHeight: 48,
              px: 1.5,
              py: 0.5,
              border: "1px solid",
              borderColor:
                admissionType === option.value
                  ? "primary.main"
                  : "divider",
              borderRadius: 2,
              bgcolor:
                admissionType === option.value
                  ? "action.selected"
                  : "background.paper",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: "primary.main",
              },
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                fontWeight: admissionType === option.value ? 600 : 400,
              },
            }}
          />
        ))}
      </RadioGroup>

      {admissionType === "selected" && (
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Admission number</InputLabel>
          <Select
            value={selectedAdmissionNo}
            label="Admission number"
            onChange={(event) =>
              setSelectedAdmissionNo(String(event.target.value))
            }
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 320, borderRadius: 2 },
              },
            }}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">Select admission</MenuItem>

            {loadingAdmissions ? (
              <MenuItem disabled>Loading admissions...</MenuItem>
            ) : (
              admissionOptions.map((item) => (
                <MenuItem
                  key={item.admission_no}
                  value={item.admission_no}
                >
                  {item.admission_no} — {item.student_name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}
    </Box>



    {/* Date Range */}
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        Outstanding Fee Date Range
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 2 }}
      >
        Select the period for the outstanding fee report.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          alignItems: "stretch",
          "& > *": {
            flex: "1 1 0",
            minWidth: 0,
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="From date"
          type="date"
          value={fromDate}
          onChange={(event) => setFromDate(event.target.value)}
          inputRef={fromDateRef}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: toDate || undefined }}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 44,
              borderRadius: 2,
            },
          }}
        />

        <TextField
          fullWidth
          size="small"
          label="To date"
          type="date"
          value={toDate}
          onChange={(event) => setToDate(event.target.value)}
          inputRef={toDateRef}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: fromDate || undefined }}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 44,
              borderRadius: 2,
            },
          }}
        />
      </Box>
    </Box>
  </Box>
</FilterModal>


      <PdfColorModal reportTitle={"Outstanding Fee Report"}
        open={colorOpen}
        onClose={() => setColorOpen(false)}
        onGenerate={generatePdf}
      />
    </Card>
  );
};

/* =========================================================
   MAIN
========================================================= */

const PaymentReport = () => {
  const [reportTab, setReportTab] =
    useState("fee-deposit");

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: {
          xs: 1.5,
          md: 2.5,
        },
      }}
    >
      <ReportTabs
        value={reportTab}
        onChange={setReportTab}
      />

      {reportTab === "fee-deposit" && (
        <FeeDepositReport />
      )}

      {reportTab === "outstanding" && (
        <OutstandingFeeReport />
      )}

    </Container>
  );
};

export default PaymentReport;
